import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "./icons";
import { getUploadUrl, uploadFile, uploadImageFromUrl } from "../lib/api";

// Save hands the rendered blob back to the parent so the modal can close
// optimistically — the S3 upload + updateReference run in the background.

/**
 * Square 1:1 cropper for library reference cards. Pointer-driven pan + zoom
 * slider, no third-party dep. The math:
 *
 *   s0 = max(F/W, F/H)         // base scale so image always covers the frame
 *   img display size = (W·s0·z, H·s0·z) at zoom z ∈ [1, ZOOM_MAX]
 *   img display center = (F/2 + tx, F/2 + ty)
 *   pan clamp: |tx| ≤ (W·s0·z − F)/2  (and symmetric for ty)
 *
 *   crop in image coords:
 *     sx = W/2 − (F/2 + tx)/(s0·z)
 *     sy = H/2 − (F/2 + ty)/(s0·z)
 *     sw = sh = F/(s0·z)
 *
 * Re-edit is symmetric: derive z + (tx, ty) from saved {x,y,w,h}.
 */

const FRAME = 360;          // crop frame size in display px
const ZOOM_MAX = 5;
const OUTPUT_SIZE = 512;    // cropped derivative side in image px

export type CropRect = { x: number; y: number; w: number; h: number };

export interface ImageEditorResult {
  /** Cropped 1:1 derivative. Parent uploads it after the modal closes. */
  blob: Blob;
  /** Content type to use when uploading `blob`. */
  contentType: string;
  /**
   * S3 key of the original (untouched) source. Empty string means we re-cropped
   * an image whose original is already persisted server-side — the parent
   * should leave the stored original alone.
   */
  originalKey: string;
  /** Crop region (0-1 relative on the original). */
  crop: CropRect;
}

interface Props {
  /** Existing original presigned URL — when present, opens straight into edit mode. */
  initialOriginalUrl?: string;
  /** Existing crop to restore the frame to. */
  initialCrop?: CropRect;
  onCancel: () => void;
  onSaved: (result: ImageEditorResult) => void;
  onError: (message: string) => void;
}

interface LoadedImage {
  el: HTMLImageElement;
  /** S3 key of the original, set after we successfully ingest it. */
  originalKey: string;
}

export function ImageEditor({
  initialOriginalUrl,
  initialCrop,
  onCancel,
  onSaved,
  onError,
}: Props) {
  const [urlInput, setUrlInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [image, setImage] = useState<LoadedImage | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const fileRef = useRef<HTMLInputElement>(null);
  const dragState = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);

  // base scale: cover the frame at zoom 1
  const s0 = useMemo(() => {
    if (!image) return 1;
    return Math.max(FRAME / image.el.naturalWidth, FRAME / image.el.naturalHeight);
  }, [image]);

  const clampPan = useCallback(
    (tx: number, ty: number, z: number = zoom): { x: number; y: number } => {
      if (!image) return { x: 0, y: 0 };
      const dispW = image.el.naturalWidth * s0 * z;
      const dispH = image.el.naturalHeight * s0 * z;
      const maxX = Math.max(0, (dispW - FRAME) / 2);
      const maxY = Math.max(0, (dispH - FRAME) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, tx)),
        y: Math.min(maxY, Math.max(-maxY, ty)),
      };
    },
    [image, s0, zoom],
  );

  // Re-clamp pan when zoom shrinks the allowed range.
  useEffect(() => {
    setPan((p) => clampPan(p.x, p.y, zoom));
  }, [zoom, clampPan]);

  // Auto-load if we already have an original (re-edit flow).
  useEffect(() => {
    if (!initialOriginalUrl) return;
    void loadImage(initialOriginalUrl, /* originalKey */ "__existing__");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOriginalUrl]);

  // After image loads, restore initialCrop if provided.
  useEffect(() => {
    if (!image || !initialCrop) return;
    const W = image.el.naturalWidth;
    const H = image.el.naturalHeight;
    // sw = rw * W → z = F / (sw * s0)
    const sw = initialCrop.w * W;
    const z = Math.max(1, Math.min(ZOOM_MAX, FRAME / (sw * s0)));
    // sx = W/2 − (F/2 + tx)/(s0·z) → tx = (W/2 − sx)·s0·z − F/2
    const sx = initialCrop.x * W;
    const sy = initialCrop.y * H;
    const tx = (W / 2 - sx) * s0 * z - FRAME / 2;
    const ty = (H / 2 - sy) * s0 * z - FRAME / 2;
    setZoom(z);
    setPan(clampPan(tx, ty, z));
    // Only run on first image load — once interaction starts, the user is in control.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  async function loadImage(src: string, originalKey: string): Promise<void> {
    setLoading(true);
    let blobUrl: string | null = null;
    try {
      // Fetch through the network (bypassing the HTTP cache) and render via a
      // same-origin blob URL. This sidesteps the classic "thumbnail cached the
      // response without CORS headers, so canvas-loading the same URL taints"
      // problem — and avoids hitting stale cache entries from before
      // crossOrigin was added to the thumbnails.
      let imageSrc = src;
      if (src.startsWith("http")) {
        const resp = await fetch(src, { cache: "reload" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status} fetching image`);
        const blob = await resp.blob();
        blobUrl = URL.createObjectURL(blob);
        imageSrc = blobUrl;
      }
      const el = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Could not load image"));
        img.src = imageSrc;
      });
      setImage({ el, originalKey });
      setZoom(1);
      setPan({ x: 0, y: 0 });
    } catch (err) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      onError(err instanceof Error ? err.message : "Failed to load image");
    } finally {
      setLoading(false);
    }
  }

  async function handleUrlLoad() {
    const url = urlInput.trim();
    if (!url) return;
    setLoading(true);
    try {
      const { key, previewUrl } = await uploadImageFromUrl(url);
      await loadImage(previewUrl, key);
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to fetch image");
      setLoading(false);
    }
  }

  async function handleFile(file: File) {
    if (!file.type.startsWith("image/")) {
      onError("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      onError("Image must be under 10 MB");
      return;
    }
    setLoading(true);
    try {
      // Upload original first so we have an S3 key to persist on save.
      const { uploadUrl, key } = await getUploadUrl(
        "reference-image-original",
        file.type,
        file.name,
      );
      await uploadFile(uploadUrl, file, file.type);
      const localUrl = URL.createObjectURL(file);
      try {
        await loadImage(localUrl, key);
      } finally {
        URL.revokeObjectURL(localUrl);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to upload image");
      setLoading(false);
    }
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!image) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    };
  }
  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPan(clampPan(dragState.current.startPanX + dx, dragState.current.startPanY + dy));
  }
  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragState.current = null;
  }

  function computeCrop(): { rect: CropRect; sourceRect: { sx: number; sy: number; sw: number; sh: number } } {
    if (!image) throw new Error("no image");
    const W = image.el.naturalWidth;
    const H = image.el.naturalHeight;
    const sw = FRAME / (s0 * zoom);
    const sh = sw;
    const sx = W / 2 - (FRAME / 2 + pan.x) / (s0 * zoom);
    const sy = H / 2 - (FRAME / 2 + pan.y) / (s0 * zoom);
    // Clamp into image bounds (defensive — clampPan should already prevent overshoot).
    const cx = Math.max(0, Math.min(W - sw, sx));
    const cy = Math.max(0, Math.min(H - sh, sy));
    return {
      sourceRect: { sx: cx, sy: cy, sw, sh },
      rect: { x: cx / W, y: cy / H, w: sw / W, h: sh / H },
    };
  }

  async function handleSave() {
    if (!image) return;
    setSaving(true);
    try {
      const { sourceRect, rect } = computeCrop();
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas unsupported");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(
        image.el,
        sourceRect.sx,
        sourceRect.sy,
        sourceRect.sw,
        sourceRect.sh,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE,
      );
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92),
      );
      if (!blob) throw new Error("Failed to render crop");

      // Re-edit flow: image.originalKey === "__existing__" means we're
      // re-cropping an already-persisted original; signal "leave it alone" with
      // an empty string to the parent.
      const originalKey = image.originalKey === "__existing__" ? "" : image.originalKey;

      // Hand the blob off and close. Parent runs the S3 upload + DDB update in
      // the background so the user isn't blocked on round-trips.
      onSaved({ blob, contentType: "image/jpeg", originalKey, crop: rect });
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save crop");
      setSaving(false);
    }
  }

  // Display geometry for the image element inside the frame.
  const dispW = image ? image.el.naturalWidth * s0 * zoom : 0;
  const dispH = image ? image.el.naturalHeight * s0 * zoom : 0;
  const imgLeft = FRAME / 2 + pan.x - dispW / 2;
  const imgTop = FRAME / 2 + pan.y - dispH / 2;

  return (
    <div className="modal-scrim" onClick={onCancel}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460 }}
      >
        <div className="modal-head">
          <div>
            <h3 className="modal-title">Edit image</h3>
            <div className="modal-sub">
              {image ? "Drag to position · use the slider to zoom" : "Paste a URL or upload a file"}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onCancel} aria-label="Close">
            <Icon.X />
          </button>
        </div>

        {!image && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void handleUrlLoad();
                  }
                }}
                placeholder="Paste image URL…"
                autoFocus
                className="field-input"
                style={{ padding: "8px 12px", flex: 1 }}
                disabled={loading}
              />
              <button
                type="button"
                className={`btn btn-line${loading || !urlInput.trim() ? " disabled" : ""}`}
                onClick={handleUrlLoad}
                disabled={loading || !urlInput.trim()}
              >
                {loading ? "…" : "Load"}
              </button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, height: 1, background: "var(--hair)" }} />
              <span style={{ fontSize: 11, color: "var(--ink-4)" }}>or</span>
              <div style={{ flex: 1, height: 1, background: "var(--hair)" }} />
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="btn btn-line"
              onClick={() => fileRef.current?.click()}
              disabled={loading}
              style={{ justifyContent: "center" }}
            >
              <Icon.Up /> Upload from device
            </button>
          </div>
        )}

        {image && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
            <div
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              style={{
                position: "relative",
                width: FRAME,
                height: FRAME,
                overflow: "hidden",
                borderRadius: 12,
                background: "var(--bg-3)",
                touchAction: "none",
                cursor: dragState.current ? "grabbing" : "grab",
                userSelect: "none",
              }}
            >
              <img
                src={image.el.src}
                alt=""
                draggable={false}
                style={{
                  position: "absolute",
                  left: imgLeft,
                  top: imgTop,
                  width: dispW,
                  height: dispH,
                  maxWidth: "none",
                  pointerEvents: "none",
                }}
              />
              {/* Crop frame outline + inner-shadow vignette so the framed area pops. */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.4)",
                  borderRadius: 12,
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}>
              <Icon.Image width={14} height={14} style={{ color: "var(--ink-3)", flexShrink: 0 }} />
              <input
                type="range"
                min={1}
                max={ZOOM_MAX}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ flex: 1, minWidth: 0 }}
              />
              <span
                style={{
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 11,
                  color: "var(--ink-3)",
                  minWidth: 36,
                  textAlign: "right",
                }}
              >
                {zoom.toFixed(2)}×
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "space-between", width: "100%" }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setImage(null);
                  setZoom(1);
                  setPan({ x: 0, y: 0 });
                  setUrlInput("");
                }}
                disabled={saving}
              >
                Replace image
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`btn btn-primary btn-sm${saving ? " disabled" : ""}`}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
